/**
 * SigsaDeclaracionRepository - Repositorio de Declaraciones SIGSA
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 *
 * Igual que LluviaRepository: delete() es SOFT-delete (marca
 * is_deleted=true y emite el UPDATE de sync), porque
 * `sigsa_declaraciones` tiene la columna is_deleted en Supabase y así
 * las anulaciones se propagan offline vía UPDATE.
 *
 * Un animal está "declarado en SIGSA" si su caravana aparece en el
 * array `caravanas` de alguna declaración no anulada de la empresa.
 */

import { v4 as uuidv4 } from 'uuid';
import { ISigsaDeclaracionRepository, SigsaDeclaracion } from '../interfaces/ISigsaDeclaracionRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/**
 * Repositorio unificado de Declaraciones SIGSA
 */
export class SigsaDeclaracionRepository
  extends BaseRepository<SigsaDeclaracion, string>
  implements ISigsaDeclaracionRepository {
  private profileRepo: IProfileRepository | null = null;

  /** Clause reutilizable para excluir anuladas (is_deleted=false). */
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'sigsa_declaraciones', 'sigsa_declaraciones');

    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;

    if (profileRepo) {
      this.profileRepo = profileRepo;
      this.setProfileRepository(profileRepo);
    }
  }

  async add(data: Omit<SigsaDeclaracion, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: SigsaDeclaracion = {
      ...data,
      id,
      created_by: data.created_by || profile?.user_id || '',
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      fecha_declaracion: data.fecha_declaracion || now,
      created_at: data.created_at || now,
      updated_at: now,
      is_deleted: false
    };

    await this.db.insert('sigsa_declaraciones', record);
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<SigsaDeclaracion> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('sigsa_declaraciones', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  /**
   * Anular declaración: soft-delete (is_deleted=true) en lugar de borrar.
   * El UPDATE viaja por la cola de sync y se propaga a Supabase (y al
   * resto de los dispositivos) aunque se haga offline.
   */
  async delete(id: string): Promise<void> {
    await this.softDelete(id);
  }

  async getByEmpresa(empresaId: string): Promise<SigsaDeclaracion[]> {
    return this.db.queryMultiple<SigsaDeclaracion>('sigsa_declaraciones', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ], { column: 'fecha_declaracion', direction: 'desc' });
  }

  async getBySession(sessionCode: string): Promise<SigsaDeclaracion[]> {
    return this.db.queryMultiple<SigsaDeclaracion>('sigsa_declaraciones', [
      this.NOT_DELETED,
      { field: 'session_code', operator: 'equals', value: sessionCode }
    ], { column: 'fecha_declaracion', direction: 'desc' });
  }

  setProfileRepository(profileRepo: IProfileRepository): void {
    this.profileRepo = profileRepo;
    super.setProfileRepository(profileRepo);
  }
}
